<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        small
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil 
        </v-icon>
      </v-btn>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Basic Salary"
      text="Update Basic Salary"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row>
          <v-col
            cols="12"
            class="pb-0"
          >
            <v-text-field
              v-model="localBasicSalary.amount"
              v-money="money"
              label="Basic Salary"
              prefix="RM"
              outlined
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="localBasicSalary.salary_type"
              v-validate="'required'"
              :error-messages="errors.collect('salaryType')"
              :items="salaryTypes"
              data-vv-name="salaryType"     
              data-vv-as="salary type"
              item-text="name"
              item-value="value"
              label="Salary Type"
              outlined
            />
          </v-col>

          <v-col
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDateDisplay"
                  v-validate="'required'"
                  :error-messages="errors.collect('start_date')"
                  data-vv-name="start_date"
                  data-vv-as="start date"
                  label="Start date"
                  class="label-position"
                  readonly
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localBasicSalary.start_date"
                no-title
                :min="minStartDate"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            v-if="localBasicSalary.end_date || companyEmployee.end_date"
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endDateDisplay"
                  label="End date"
                  class="label-position"
                  readonly
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                  <v-tooltip 
                    v-if="!localBasicSalary.end_date"
                    slot="append"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="warning"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>Only key this end date if this is the <u>last salary</u> record. Increment button <u>will not be visible</u> once end date has been updated!</span>
                  </v-tooltip>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localBasicSalary.end_date"
                no-title
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>
          
          <v-col
            v-if="localBasicSalary.is_e_letter_sent"
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="localBasicSalary.letter_type"
              v-validate="'required'"
              :error-messages="errors.collect('letterType')"
              :items="letterTypes"
              data-vv-name="letterType"     
              data-vv-as="letter type"
              item-text="name"
              item-value="value"
              label="Letter Type"
              outlined
            />
          </v-col>
          
          <v-col
            v-if="localBasicSalary.is_e_letter_sent"
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="letterDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="letterDateDisplay"
                  v-validate="'required'"
                  outlined
                  readonly
                  label="Letter Date"
                  :error-messages="errors.collect('letterDateDisplay')"
                  data-vv-name="letterDateDisplay"    
                  data-vv-as="letter date"
                  :class="{
                    'required':!letterDateDisplay
                  }"
                  class="label-position" 
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="localBasicSalary.letter_date"
                no-title
                @input="letterDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          color="hrissecondary"    
          class="white--text px-10" 
          :loading="progressing"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { VMoney } from 'v-money'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    directives: { money: VMoney },
    props: {
      basicSalary: {
        type: Object,
        required: true,
      },
      companyEmployee: {
        type: Object,
        default: null,
      },
      minStartDate: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localBasicSalary: null,
        startDateMenu: false,
        endDateMenu: false,
        letterDateMenu: false,
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false, /* doesn't work with directive */
        },
        letterTypes: [
          {
            name: 'Increment Letter',
            value: 'increment',
          },
          {
            name: 'Promotion Letter',
            value: 'promotion',
          },
        ],
        salaryType: null,
        salaryTypes: [
          {
            name: 'Monthly Basis',
            value: 'monthly',
          },
          {
            name: 'Daily Basis',
            value: 'daily',
          },
        ],
      }
    },
    computed: {
      startDateDisplay () {
        return this.localBasicSalary.start_date ? this.$moment(this.localBasicSalary.start_date).format('DD MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.localBasicSalary.end_date ? this.$moment(this.localBasicSalary.end_date).format('DD MMM YYYY') : ''
      },
      letterDateDisplay () {
        return this.localBasicSalary.letter_date ? this.$moment(this.localBasicSalary.letter_date).format('DD MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localBasicSalary = { ...this.basicSalary }

        if (this.localBasicSalary.amount) { this.localBasicSalary.amount = (Math.round((this.localBasicSalary.amount / 100) * 100) / 100).toFixed(2) }
        // if (this.localBasicSalary.start_date) { this.localBasicSalary.start_date = this.localBasicSalary.start_date.substring(0, 7) }
        // if (this.localBasicSalary.end_date) { this.localBasicSalary.end_date = this.localBasicSalary.end_date.substring(0, 7) }
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            this.setProgressing(true)

            this.$api.companyEmployeeSalaries().update(this.basicSalary.id, this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })
                this.$emit('updated')

                this.setProgressing(false)
                this.dialog = false
              }).catch(err => {
                if (err.response.status >= 400 && err.response.status < 500) {
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.message, 
                  })  
                }

                this.setProgressing(false)
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        return {
          start_date: this.$moment(this.localBasicSalary.start_date) < this.$moment(this.companyEmployee.start_date) ? this.companyEmployee.start_date : this.localBasicSalary.start_date,
          end_date: this.localBasicSalary.end_date,
          amount: this.localBasicSalary.amount.toString().replaceAll(',', ''),
          letter_date: this.localBasicSalary.letter_date,
          letter_type: this.localBasicSalary.letter_type,
          salary_type: this.localBasicSalary.salary_type,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
